import type { DependenciesUploadStatus, PremiereClipInfo, PremiereClipInfoMap } from '@mimir/client-common/fileSync/premiereTypes';
import type { SequenceExportInfo } from '@mimir/primitive/premiere/sequenceDetails';
import type { UploadOps } from '@mimir/client-common/fileSync/uploadOps';
import type { UploadItem } from '@mimir/client-common/upload/UploadItem';
import { getNonMimirClipsToUploadWith, getOngoingUploadForClipWith } from '@mimir/client-common/upload/premiere';
import { asyncTimeout } from '@mimir/common/async/asyncTimeout';
import type { AdobePanelContext, ApplicationContext, ElectronContext } from '@mimir/common/context/applicationContext';
import type { Logger } from '@mimir/common/types/logger';
import type { BasicUploadManager } from '@mimir/common/upload/BasicUploadManager';
import { getLocalFileSize } from '@mimir/common/util/fs';
import type { CancelToken } from 'axios';
import Vue from 'vue';
import { storeDispatchInsertUploadItemIntoSearch } from '../store';
import { storeDispatchDoUpload } from '../store/storeDispatch';
import type { QuickPreview } from '@mimir/web/model/quickPreview';

export class DefaultUploadOps implements UploadOps {

  constructor(
    private applicationContext: AdobePanelContext | ElectronContext,
    private logger: Logger,
    private uploadManager: BasicUploadManager,
    private quickPreview: QuickPreview,
    private premiereClipInfos: PremiereClipInfoMap,
    private ongoingUploads: UploadItem[],
    private isProxyMode: boolean,
  ) {}


  async trackUploads(
    uploadAllPremiereClips: boolean,
    sequenceExportInfo: SequenceExportInfo | undefined,
    totalDependencies: number,
    dependenciesUploadStatus: DependenciesUploadStatus,
    allNonMimirClips: PremiereClipInfo[],
    totalHighresSize: number,
    cancelToken: CancelToken,
  ): Promise<void> {
    return await trackUploads({
      applicationContext: this.applicationContext,
      logger: this.logger,
    }, this.premiereClipInfos, this.ongoingUploads, uploadAllPremiereClips, sequenceExportInfo, totalDependencies, dependenciesUploadStatus, allNonMimirClips, totalHighresSize, cancelToken);
  }


  async trackAndUploadItemMedia(uploadItem: UploadItem): Promise<void> {
    const doUploadPayload: {
      isProxyMode: boolean,
      uploadManager: BasicUploadManager,
      applicationContext: ApplicationContext,
      items: UploadItem[],
      inject: boolean,
      quickPreview: QuickPreview,
    } = {
      uploadManager: this.uploadManager,
      applicationContext: this.applicationContext,
      items: [uploadItem],
      inject: false,
      isProxyMode: this.isProxyMode,
      quickPreview: this.quickPreview,
    };

    return storeDispatchDoUpload(doUploadPayload);
  }


  onPremiereClipUploadStart(uploadItem: UploadItem, injectItemIntoSearch: boolean) {
    if (injectItemIntoSearch) {
      storeDispatchInsertUploadItemIntoSearch({
        applicationContext: this.applicationContext,
        uploadItem,
        isProxyMode: this.isProxyMode,
        addRouterLink: false,
        quickPreview: this.quickPreview,
      });
    }
  }

}


async function trackUploads(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  premiereClipInfos: PremiereClipInfoMap,
  ongoingUploads: UploadItem[],
  uploadAllPremiereClips: boolean,
  sequenceExportInfo: SequenceExportInfo | undefined,
  totalDependencies: number,
  dependenciesUploadStatus: any,
  allNonMimirClips: PremiereClipInfo[],
  totalHighresSize: number,
  cancelToken: CancelToken,
): Promise<void> {
  let needToWait = false;
  do {
    const remainingNonMimirClips = getNonMimirClipsToUploadWith(premiereClipInfos, uploadAllPremiereClips, sequenceExportInfo);
    const remainingDependencies = remainingNonMimirClips.length;
    needToWait = remainingDependencies > 0;

    if (needToWait) {
      services.logger.debug(`still to upload: ${JSON.stringify(remainingNonMimirClips.map(clipInfo => clipInfo.mediaPath), null, 2)}`);
      const doneDependencies = totalDependencies - remainingDependencies;
      Vue.set(dependenciesUploadStatus, 'doneDependencies', doneDependencies);

      const totalUploadedSize = getTotalUploadedSize(services, ongoingUploads, allNonMimirClips);
      const progressPercentage = totalHighresSize > 0 ? Math.min(totalUploadedSize * 100 / totalHighresSize, 100) : 0;
      Vue.set(dependenciesUploadStatus, 'progressPercentage', progressPercentage);
      services.logger.debug(`totalUploadedSize: ${totalUploadedSize}, totalHighresSize: ${totalHighresSize}, progressPercentage: ${progressPercentage}`);
      await asyncTimeout(3000);
    }
  } while (needToWait && !cancelToken.reason);
}


function getTotalUploadedSize(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  ongoingUploads: UploadItem[],
  clipInfos: PremiereClipInfo[],
) {
  let totalUploadedSize = 0;
  for (const clipInfo of clipInfos) {
    const totalBytes = getClipHighresSizeOrZero(services, clipInfo);
    let thisClipDoneBytes = 0;

    const ongoingUpload = getOngoingUploadForClipWith(ongoingUploads, clipInfo, false);

    if (ongoingUpload) {
      thisClipDoneBytes = totalBytes * (ongoingUpload.uploadProgress || 0) / 100;
    } else {
      thisClipDoneBytes = totalBytes;
    }

    services.logger.debug(`${clipInfo.name}: ongoingUpload.uploadProgress: ${ongoingUpload?.uploadProgress}, total: ${totalBytes}, done: ${thisClipDoneBytes}`);

    totalUploadedSize += thisClipDoneBytes;
  }

  return totalUploadedSize;
}


function getClipHighresSizeOrZero(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  clipInfo: PremiereClipInfo,
): number {
  let fileSize = clipInfo?.mimirItemLocalMediaStates?.highresState?.fileSize;
  if (!fileSize) {
    try {
      fileSize = getLocalFileSize(services.applicationContext.nodeApi, clipInfo?.mediaPath);
    } catch (err) {
      // do not rethrow since that can fail the ongoing upload operation
      services.logger.error(`Error getting size from ${clipInfo.mediaPath}: ${err}`);
    }
  }

  return fileSize || 0;
}
